import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Code2, Copy, Check, Terminal, Globe, Smartphone } from 'lucide-react'
import { OrganizationContext } from './AssistPage'

export function SignalsCodePage() {
  const orgContext = useOutletContext<OrganizationContext>()
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Just set loading to false since we get org ID from context
    setLoading(false)
  }, [orgContext?.organizationId])

  const copyToClipboard = async (text: string, snippetId: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedSnippet(snippetId)
    setTimeout(() => setCopiedSnippet(null), 2000)
  }

  const apiKeyPlaceholder = 'YOUR_API_KEY'

  const codeSnippets = {
    javascript: {
      install: `npm install @logg/signals`,
      setup: `import { Signals } from '@logg/signals'

// Initialize the client
const signals = new Signals({
  apiKey: '${apiKeyPlaceholder}',
  debug: true // Enable for development
})`,
      identify: `// Set user context after login (called once)
signals.identify('user-123')

// Set screen/surface context when navigating
signals.setContext({
  screen: 'product_detail',
  surface: 'market_feed',
  entry_point: 'search_results'
})

// Update context incrementally
signals.updateContext({ referrer_surface: 'home_feed' })

// Reset on logout
signals.reset()`,
      trackEvent: `// Events follow: Actor (user) → Action (type) → Target
// Use simple verbs when obvious from target
// Use action:subtype when disambiguation needed

// Simple action - "viewed" is clear from target type
await signals.event({
  type: 'viewed',
  target: { type: 'product', id: 'prod-123' },
  productType: 'Watch',
  listedPriceInCents: 1500000
})

// Action:subtype - "added" needs context (added where?)
await signals.event({
  type: 'added:cart',
  target: { type: 'product', id: 'prod-123' },
  quantity: 1,
  priceInCents: 1500000
})

// Simple action - "followed" is clear from target
await signals.event({
  type: 'followed',
  target: { type: 'user', id: 'user-456' }
})

// No target needed for search
await signals.event({
  type: 'search',
  query: 'rolex submariner',
  resultCount: 24
})`,
    },
    react: {
      setup: `import { Signals } from '@logg/signals'

// Create a signals instance (do this once)
const signals = new Signals({
  apiKey: '${apiKeyPlaceholder}'
})

// Set user after auth
export function onLogin(user: User) {
  signals.identify(user.id)
}

// Reset on logout
export function onLogout() {
  signals.reset()
}

export { signals }`,
      hook: `// Use in your components
import { signals } from './lib/signals'

function ProductPage({ product }) {
  useEffect(() => {
    // Set screen context
    signals.setContext({
      screen: 'product_detail',
      surface: 'market_feed'
    })

    // Simple "viewed" - target makes it clear
    signals.event({
      type: 'viewed',
      target: { type: 'product', id: product.id },
      productType: product.type,
      listedPriceInCents: product.priceInCents
    })
  }, [product.id])

  const handleAddToCart = () => {
    // "added:cart" - subtype clarifies where it was added
    signals.event({
      type: 'added:cart',
      target: { type: 'product', id: product.id },
      priceInCents: product.priceInCents,
      quantity: 1
    })
  }

  return (
    <button onClick={handleAddToCart}>
      Add to Cart
    </button>
  )
}`,
    },
    node: {
      setup: `import { Signals } from '@logg/signals'

const signals = new Signals({
  apiKey: '${apiKeyPlaceholder}'
})

// Middleware to set user context from request
app.use((req, res, next) => {
  if (req.user?.id) {
    signals.identify(req.user.id)
  }
  signals.setContext({
    requestId: req.id,
    surface: req.headers['x-surface']
  })
  next()
})

// "purchased" - clear from order target
app.post('/api/purchase', async (req, res) => {
  const { orderId, items } = req.body

  await signals.event({
    type: 'purchased',
    target: { type: 'order', id: orderId },
    items: items.map(item => ({
      productId: item.productId,
      quantity: item.quantity
    }))
  })

  res.json({ success: true })
})

// "saved" - clear from product target
app.post('/api/products/:id/save', async (req, res) => {
  await signals.event({
    type: 'saved',
    target: { type: 'product', id: req.params.id }
  })

  res.json({ success: true })
})

// Flush on shutdown
process.on('beforeExit', async () => {
  await signals.destroy()
})`,
    },
    curl: {
      basic: `# Simple "viewed" action - target makes it clear
curl -X POST https://api.logg.com/events \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyPlaceholder}" \\
  -d '{
    "events": [{
      "type": "viewed",
      "target": { "type": "product", "id": "prod-123" },
      "listedPriceInCents": 1500000
    }],
    "user": {
      "user_id": "user-123",
      "session_id": "sess-456"
    },
    "context": {
      "screen": "product_detail",
      "surface": "web"
    }
  }'`,
      batch: `# Multiple events with simple action types
curl -X POST https://api.logg.com/events \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyPlaceholder}" \\
  -d '{
    "events": [
      { "type": "search", "query": "rolex" },
      {
        "type": "viewed",
        "target": { "type": "product", "id": "prod-123" }
      },
      {
        "type": "added:cart",
        "target": { "type": "product", "id": "prod-123" },
        "quantity": 1
      }
    ],
    "user": {
      "user_id": "user-123",
      "session_id": "sess-456"
    },
    "context": {
      "screen": "product_detail",
      "surface": "ios_app"
    }
  }'`,
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Code2 className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Integration Code</h1>
          <p className="text-muted-foreground">
            Code snippets to integrate Signals into your application
          </p>
        </div>
      </div>

      <Tabs defaultValue="javascript" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="javascript" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            JavaScript
          </TabsTrigger>
          <TabsTrigger value="react" className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            React
          </TabsTrigger>
          <TabsTrigger value="node" className="flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Node.js
          </TabsTrigger>
          <TabsTrigger value="curl" className="flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            cURL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="javascript" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Install the Signals SDK via npm or yarn</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.javascript.install}
                snippetId="js-install"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Initialize</CardTitle>
              <CardDescription>Create the Signals client instance</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.javascript.setup}
                snippetId="js-setup"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User & Event Context</CardTitle>
              <CardDescription>Set context once instead of passing with every event</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.javascript.identify}
                snippetId="js-identify"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Track Events</CardTitle>
              <CardDescription>Actor (user) → Action (type) → Target (object acted upon)</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.javascript.trackEvent}
                snippetId="js-track"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="react" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Setup with Auth Handlers</CardTitle>
              <CardDescription>Initialize and manage user context with login/logout</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.react.setup}
                snippetId="react-setup"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Component Integration</CardTitle>
              <CardDescription>Set screen context and track events in components</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.react.hook}
                snippetId="react-hook"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="node" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Server-Side with Middleware</CardTitle>
              <CardDescription>Set user context from request and track events</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.node.setup}
                snippetId="node-setup"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="curl" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Request</CardTitle>
              <CardDescription>Send a single event via the REST API</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.curl.basic}
                snippetId="curl-basic"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Batch Request</CardTitle>
              <CardDescription>Send multiple events in a single request</CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={codeSnippets.curl.batch}
                snippetId="curl-batch"
                copiedSnippet={copiedSnippet}
                onCopy={copyToClipboard}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function CodeBlock({ 
  code, 
  snippetId, 
  copiedSnippet, 
  onCopy 
}: { 
  code: string
  snippetId: string
  copiedSnippet: string | null
  onCopy: (text: string, id: string) => void
}) {
  return (
    <div className="relative">
      <pre className="rounded-lg bg-muted p-4 overflow-x-auto">
        <code className="text-sm font-mono whitespace-pre">{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2"
        onClick={() => onCopy(code, snippetId)}
      >
        {copiedSnippet === snippetId ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>
    </div>
  )
}
